import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { namespace } from '@common-config/constants';
import { shouldAutoOpenOnboarding } from './onboarding-logic';
import { C, ONB_CSS } from './onboarding-ui';
import { useSetupStatus } from './use-setup-status';
import { GuideVideoButton } from '@/common/components/guide-video/guide-video-button';

/**
 * "Finish setup" in the web sidebar — the way back into the connect wizard until the user is
 * connected AND their first message went out. The same conditions as the wizard, so the bar
 * shows exactly the wizard's progress. Hidden while the live state settles (no flash for a
 * set-up user) and once done.
 */
export const SetupProgress: React.FC<{ minimized: boolean }> = ({ minimized }) => {
  const { t, i18n } = useTranslation(namespace);
  const navigate = useNavigate();
  const { ready, done, progress, next } = useSetupStatus();
  if (!ready || done || !next) return null;

  const title = t('onboarding.finishSetup');
  const step = t('onboarding.progress.meter', { current: progress.step });
  // Say exactly what clicking does: the next action, not a generic label.
  const action = t(`onboarding.next.${next}`);
  const open = () => navigate('/onboarding?from=sidebar');
  const dir = i18n.dir(i18n.resolvedLanguage || i18n.language);

  return (
    <div className="onb-root" dir={dir} style={{ padding: minimized ? '4px 0 8px' : '2px 12px 10px', display: 'flex', justifyContent: 'center' }}>
      <style>{ONB_CSS}</style>
      {minimized ? (
        // A ring that fills with the progress; the step number in the middle.
        <button type="button" className="onb-setup" onClick={open} aria-label={`${title} · ${action}`}
          style={{ position: 'relative', width: 40, height: 40, borderRadius: 999, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.inkText, fontSize: 12.5, fontWeight: 800, fontFamily: 'inherit' }}>
          <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}>
            <circle cx="20" cy="20" r="16" fill="none" stroke="var(--onb-ink-soft)" strokeWidth="4" />
            <circle cx="20" cy="20" r="16" fill="none" stroke="var(--onb-ink)" strokeWidth="4" strokeLinecap="round" pathLength={100} strokeDasharray={`${progress.pct} 100`} style={{ transition: 'stroke-dasharray .6s cubic-bezier(.2,.8,.2,1)' }} />
          </svg>
          {progress.step}
        </button>
      ) : (
        // A div, not a <button>: it holds the lesson ▶, and buttons cannot nest.
        <div role="button" tabIndex={0} className="onb-setup" onClick={open}
          onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); open(); } }}
          style={{ width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 9, padding: '11px 12px', borderRadius: 12, border: '1px solid color-mix(in srgb, var(--onb-ink) 28%, transparent)', background: 'var(--onb-ink-soft)', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'start' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
            <span aria-hidden className="onb-pulse" style={{ width: 8, height: 8, borderRadius: 999, background: C.ink, flex: '0 0 auto' }} />
            <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 700, color: C.text }}>{title}</span>
            <GuideVideoButton lesson="get-started" variant="icon" size={16} surface="sidebar_finish_setup" />
            <span style={{ fontSize: 11.5, fontWeight: 600, color: C.muted, whiteSpace: 'nowrap' }}>{step}</span>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', fontSize: 13, fontWeight: 650, color: C.inkText }}>
            <span style={{ flex: 1, minWidth: 0 }}>{action}</span>
            <ArrowRight size={14} strokeWidth={2.5} className="bt-arrow" style={{ flex: '0 0 auto' }} />
          </span>
          <span aria-hidden style={{ width: '100%', height: 5, borderRadius: 999, background: C.track, overflow: 'hidden' }}>
            <span className="onb-meter" style={{ display: 'block', height: '100%', width: `${progress.pct}%`, borderRadius: 999, background: C.brand }} />
          </span>
        </div>
      )}
    </div>
  );
};

/** On /calendar: open the wizard by itself exactly once per account (never seen it, setup not
 *  done) — after the user had 3 seconds with their calendar. Renders nothing. */
export const OnboardingAutoOpen: React.FC = () => {
  const navigate = useNavigate();
  const { ready, done, seen } = useSetupStatus();
  const open = shouldAutoOpenOnboarding({ ready, done, seen });
  useEffect(() => {
    if (!open) return undefined;
    const id = window.setTimeout(() => navigate('/onboarding?from=calendar'), 3000);
    return () => window.clearTimeout(id);
  }, [open, navigate]);
  return null;
};
